import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { AssayStr, GeneGrouping, L2Target, L2Targets, sixteenS } from '@resistapp/common/assays';
import {
  AllProjectSampleTypesGroup,
  SampleTypeGroup,
  allSampleGroupTypes,
  filterGroupSamplingPoints,
  getComparableSamplingPointGroups,
} from '@resistapp/common/comparable-sample-groups';
import {
  DEFAULT_END_INTERVAL,
  DEFAULT_START_INTERVAL,
  ensureLocalStartOfMonth,
  ensureLocalStartOfNextMonth,
  ensureLocalValidMidnightOrDefault,
  type StandardDateFormat,
} from '@resistapp/common/friendly';
import { hasNormalisationModeData, isConsideredAbsolute } from '@resistapp/common/normalisation-mode';
import { FullProject, FullSamplesByUID, NormalisationMode, SamplingPoint, XAxisMode } from '@resistapp/common/types';
import {
  flattenRelevantAbundances,
  flattenSamplesByUID,
  sortUniqSamplingPointsAndAddSampleStatus,
} from '@resistapp/common/utils';
import { format } from 'date-fns';
import { get, keyBy as groupBy, uniq, values } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useLocalStorageState from 'use-local-storage-state';
import {
  AbunanceSelection,
  Filters,
  filterSamplesAndAbundances,
  getNextToggledIdentifiers,
} from '../../data-utils/filter-data/filter';
import {
  canUseResearchHeatmapGeneSetSelector,
  getEffectiveResearchHeatmapAbundanceMode,
  requiresResearchHeatmapEstimationsParam,
} from '../../utils/research-heatmap-abundance-mode';
import {
  LegacyQueryParams,
  QueryParams,
  getChipsParams,
  getGeneGroupingParams,
  getGeneGroupsParams,
  getNormalisationModeParam,
  getSamplingPointTypeParam,
  getSamplingPointsParams,
  getXAxisModeParam,
  mutateSamplingPointRelatedSearchParams,
  mutateSamplingPointScopeSearchParams,
  mutateSearchParamsWithSelection,
} from '../../utils/url-manipulation';

export interface UseQueryFilters {
  focusSamplesByUID: (samplesByUID: FullSamplesByUID) => FullSamplesByUID | undefined;
  queryFilters: QueryFilters;
}

type ToggleGeneGroup = (
  group: L2Target | L2Target[] | AssayStr,
  removeOldSelections: boolean,
  selectedGroupingInCaseStateHasNotUpdated?: GeneGrouping,
) => void;

export interface QueryFilters {
  setGroupingStable: (grouping: GeneGrouping | null) => void;
  setSampleTypeGroupStable: (type: SampleTypeGroup, replaceAndClearSmplPnts: boolean) => void;
  setSamplingPointScopeStable: (group: SampleTypeGroup, samplingPointIds?: number[]) => void;
  toggleSamplingPointStable: (id: number | number[] | undefined, removeOldSelections: boolean) => void;
  setChipStable: (chipId: number | undefined) => void;
  setMatchingSamplingPointsAcrossTypes: (search: string) => void;
  hasFocus: boolean;
  toggleGeneGroupStable: ToggleGeneGroup;
  toggleSingleTarget: (target: string) => void;
  filters: Filters;
  setAbundanceModeStable: (mode: AbunanceSelection) => void;
  setNormalisationModeStable: (value: NormalisationMode) => void;
  resetFiltersStable: () => void;
  setXAxisModeStable: (mode: XAxisMode) => void;
  setIntervalStable: (start: Date | StandardDateFormat, end: Date | StandardDateFormat) => void;
  setMonthStable: (month: Date | null, endDate: Date | null) => void;
}

const defaultAbundanceMode = AbunanceSelection.ANALYSED;

function useFiltersInternal() {
  const { allGeneGroups } = useAssayContext();
  const { user, userLoading } = useUser();
  const [isAdminModeActive] = useLocalStorageState(LocalStorageId.adminMode, {
    defaultValue: false,
  });
  const [abundanceMode, setAbundanceModeStable] = useLocalStorageState(LocalStorageId.abundanceMode, {
    defaultValue: defaultAbundanceMode,
  });
  const canUseHeatmapGeneSetSelector = canUseResearchHeatmapGeneSetSelector(user, isAdminModeActive);
  const effectiveAbundanceMode = userLoading
    ? abundanceMode
    : getEffectiveResearchHeatmapAbundanceMode(abundanceMode, canUseHeatmapGeneSetSelector);

  const { searchParams } = useSearchParamsContext();
  const start = ensureLocalValidMidnightOrDefault(
    searchParams.get(QueryParams.START) as StandardDateFormat,
    DEFAULT_START_INTERVAL,
  );
  const end = ensureLocalValidMidnightOrDefault(
    searchParams.get(QueryParams.END) as StandardDateFormat,
    DEFAULT_END_INTERVAL,
  );

  const [allProjectSamplingPoints, setAllProjectSamplingPointsStable] = useState<SamplingPoint[]>([]);
  const [allProjectSamplingPointNames, setAllProjectSamplingPointNamesStable] = useState<string[]>([]);
  const [allProjectSamplingPointIds, setAllProjectSamplingPointIdsStable] = useState<number[]>([]);
  const [allProjectChipIds, setAllProjectChipIdsStable] = useState<number[]>([]);

  // Determine the X-axis mode from the query params. If none is provided, fall back to the
  // legacy default (but allow it to be overwriden by project default below),
  // ensuring that downstream logic always receives a concrete value.
  const queryXAxisModeRaw = useXAxisMode();
  const queryXAxisMode: XAxisMode = queryXAxisModeRaw ?? XAxisMode.ORDINAL_GROUPED;
  const geneGroupsParam = searchParams.get(QueryParams.GENE_GROUPS);
  const queryGeneGroups = useMemo(() => getGeneGroupsParams(geneGroupsParam), [geneGroupsParam]);
  const queryGeneGrouping = getGeneGroupingParams(searchParams, allGeneGroups);

  const samplingPointsParam =
    searchParams.get(QueryParams.SAMPLING_POINTS) ?? searchParams.get(LegacyQueryParams.ENVIRONMENTS);
  const querySamplingPointIds = useMemo(
    () => getSamplingPointsParams(samplingPointsParam, false),
    [samplingPointsParam],
  );
  const chipsParam = searchParams.get(QueryParams.CHIPS);
  const queryChipIds = useMemo(() => getChipsParams(chipsParam), [chipsParam]);
  const querySamplingPointGroup =
    getSamplingPointTypeParam(searchParams) ?? AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS;

  const queryNormalisationMode = getNormalisationModeParam(searchParams);
  useEffect(() => {
    // Convert legacy value
    if ((abundanceMode as unknown) === 'ANALYZED') {
      setAbundanceModeStable(AbunanceSelection.ANALYSED);
    }
  }, [abundanceMode, setAbundanceModeStable]);

  const environmentsImplicitlySelectedWithSampleTypeGroupSelection = useMemo(
    () => filterGroupSamplingPoints(allProjectSamplingPoints, querySamplingPointGroup),
    [allProjectSamplingPoints, querySamplingPointGroup],
  );

  // Memoize selected sampling point names array
  // NOTE: this is project dependent and always runs after project is loaded
  // and this behaviour is exected by ResearchProvider for determining when to buildResearchPlotData
  // NOTE: for an implicit site view in a signle rpocess site project, and treated WW selected,
  //       this only ends up implicitly selecting one of the two site sampling points
  //       whereas we would normally have both of the process site invs in the query.
  //       This special case may need some consideration elsewhere, at least in useSelectedSiteDatum.
  const selectedSamplingPointIdsOrdered = useMemo(
    () =>
      querySamplingPointIds.length
        ? querySamplingPointIds
        : environmentsImplicitlySelectedWithSampleTypeGroupSelection.map(e => e.id),
    [querySamplingPointIds, environmentsImplicitlySelectedWithSampleTypeGroupSelection],
  );

  const selectedChips = useMemo(
    () => (queryChipIds.length ? queryChipIds : allProjectChipIds),
    [queryChipIds, allProjectChipIds],
  );

  // Memoize selected targets array
  const selectedTargets = useMemo(
    () => (queryGeneGroups.length ? queryGeneGroups : allGeneGroups[queryGeneGrouping]) as L2Targets[],
    [queryGeneGroups, allGeneGroups, queryGeneGrouping],
  );

  // Memoize interval object
  const interval = useMemo(
    () => ({
      start,
      end,
    }),
    // This warning is ok, but for some reason eslint does not allow surpresing it
    // eslint-disable-next-line exhaustive-deps-with-refs/exhaustive-deps
    [start.getTime(), end.getTime()],
  );

  // A convenience object containing the active query params
  // or all values when multi-select query params are not set
  // NOTE: each member is separately memoized for safe referencing in dependency arrays.
  // NOTE! NEVER use the full filters object in dependency arrays.
  const filters = useMemo<Filters>(
    () => ({
      selectedSampleTypeGroup: querySamplingPointGroup,
      selectedSamplingPointIdsOrdered,
      chips: selectedChips,
      selectedTargetGrouping: queryGeneGrouping,
      selectedTargets,
      interval,
      xAxisMode: queryXAxisMode,
      normalisationMode: queryNormalisationMode,
      abundances: effectiveAbundanceMode,
    }),
    [
      querySamplingPointGroup,
      selectedSamplingPointIdsOrdered,
      selectedChips,
      queryGeneGrouping,
      selectedTargets,
      interval,
      queryXAxisMode,
      queryNormalisationMode,
      effectiveAbundanceMode,
    ],
  );

  return {
    filters,
    storedAbundanceMode: abundanceMode,
    heatmapGeneSetAccessResolved: !userLoading,
    setAllProjectSamplingPointsStable,
    setAllProjectSamplingPointNamesStable,
    allProjectSamplingPoints,
    environmentsImplicitlySelectedWithSampleTypeGroupSelection,
    querySamplingPointIds,
    queryChipIds,
    queryGeneGroups,
    queryGeneGrouping,
    queryXAxisMode,
    setAbundanceModeStable,
    allProjectSamplingPointNames,
    setAllProjectSamplingPointIdsStable,
    allProjectSamplingPointIds,
    setAllProjectChipIdsStable,
    allProjectChipIds,
  };
}

export function useNormalisationMode(): NormalisationMode {
  const { searchParams } = useSearchParamsContext();
  return useMemo(() => getNormalisationModeParam(searchParams), [searchParams]);
}

export function useXAxisMode(): XAxisMode | undefined {
  const { searchParams } = useSearchParamsContext();
  return useMemo(() => getXAxisModeParam(searchParams), [searchParams]);
}

export function useFilters(): Filters {
  const { filters } = useFiltersInternal();
  return filters;
}

type PlottableAbundance = ReturnType<typeof flattenRelevantAbundances>[number];

function hasModeResult(abundance: PlottableAbundance, mode: NormalisationMode): boolean {
  return hasNormalisationModeData(abundance, mode);
}

export function getModeCompatibleResearchAssays(samplesByUID: FullSamplesByUID, mode: NormalisationMode): Set<string> {
  return new Set(
    flattenRelevantAbundances(flattenSamplesByUID(samplesByUID))
      .filter(abundance => hasModeResult(abundance, mode))
      .map(abundance => abundance.assay),
  );
}

export function useQueryFilters(project: FullProject | undefined): UseQueryFilters {
  const { setSearchParamsStable, searchParamsRef } = useSearchParamsContext();
  const { allGeneGroups, getGroup, assaysLoaded } = useAssayContext();
  const {
    filters,
    storedAbundanceMode,
    heatmapGeneSetAccessResolved,
    setAllProjectSamplingPointsStable,
    setAllProjectSamplingPointNamesStable,
    allProjectSamplingPoints,
    environmentsImplicitlySelectedWithSampleTypeGroupSelection,
    querySamplingPointIds,
    queryGeneGroups,
    queryGeneGrouping,
    queryChipIds,
    setAbundanceModeStable,
    allProjectSamplingPointNames,
    setAllProjectSamplingPointIdsStable,
    allProjectSamplingPointIds,
    setAllProjectChipIdsStable,
    allProjectChipIds,
  } = useFiltersInternal();
  const location = useLocation();

  const [allProjectSampleTypes, setAllProjectSampleTypes] = useState<SampleTypeGroup[]>([]);

  useEffect(() => {
    if (!heatmapGeneSetAccessResolved) {
      return;
    }
    if (storedAbundanceMode === filters.abundances) {
      return;
    }

    setAbundanceModeStable(filters.abundances);
  }, [heatmapGeneSetAccessResolved, storedAbundanceMode, filters.abundances, setAbundanceModeStable]);

  useEffect(() => {
    if (!heatmapGeneSetAccessResolved) {
      return;
    }
    const requiresEstimations = requiresResearchHeatmapEstimationsParam(filters.abundances);
    const hasEstimationsParam = searchParamsRef.current.has('estimations');
    const hasTrueEstimationsParam = searchParamsRef.current.get('estimations') === 'true';

    if (requiresEstimations && hasTrueEstimationsParam) {
      return;
    }

    if (!requiresEstimations && !hasEstimationsParam) {
      return;
    }

    if (requiresEstimations) {
      searchParamsRef.current.set('estimations', 'true');
    } else {
      searchParamsRef.current.delete('estimations');
    }
    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, [heatmapGeneSetAccessResolved, filters.abundances, searchParamsRef, setSearchParamsStable]);

  useEffect(() => {
    const legacySamplingPoints = searchParamsRef.current.get(LegacyQueryParams.ENVIRONMENTS);
    const legacySamplingPointType = searchParamsRef.current.get(LegacyQueryParams.ENVIRONMENT_TYPE);

    if (!legacySamplingPoints && !legacySamplingPointType) {
      return;
    }

    if (legacySamplingPointType) {
      getSamplingPointTypeParam(searchParamsRef.current);
    }

    if (legacySamplingPoints) {
      getSamplingPointsParams(searchParamsRef.current, false);
    }

    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, [searchParamsRef, setSearchParamsStable]);

  // Capture whether the x-axis mode was explicitly provided in the URL. This allows us
  // to respect project-level defaults only when the user hasn't already chosen a mode.
  const rawXAxisModeParam = useXAxisMode();

  useEffect(() => {
    if (!project) {
      return;
    }

    const projectSamplingPoints = sortUniqSamplingPointsAndAddSampleStatus(project.samplesByUID, project.id);
    const allSamplingPointIds = projectSamplingPoints.map(smplPnt => smplPnt.id);
    const allSamplingPointNames = projectSamplingPoints.map(smplPnt => smplPnt.name);

    const allChipIds = project.chips.map(chip => chip.id);

    // Get both regular smplPnt types and comparable groups
    const comparableGroups = getComparableSamplingPointGroups(projectSamplingPoints, undefined);
    const allProjectSampleTypesToBeSet = uniq([
      ...comparableGroups.map(g => g.type),
      ...projectSamplingPoints.map(smplPnt => smplPnt.type),
    ]);

    setAllProjectSampleTypes(allProjectSampleTypesToBeSet);
    setAllProjectSamplingPointsStable(projectSamplingPoints);
    setAllProjectSamplingPointNamesStable(allSamplingPointNames);
    setAllProjectSamplingPointIdsStable(allSamplingPointIds);
    setAllProjectChipIdsStable(allChipIds);
  }, [project]);

  useEffect(() => {
    if (!project || queryGeneGrouping !== 'assay' || !queryGeneGroups.length) {
      return;
    }

    const mode = filters.normalisationMode;
    const modeCompatibleAssays = getModeCompatibleResearchAssays(project.samplesByUID, mode);

    const selectedAssays = queryGeneGroups.map(String);
    const validSelectedAssays = selectedAssays.filter(assay => modeCompatibleAssays.has(assay));
    if (selectedAssays.length === validSelectedAssays.length) {
      return;
    }

    const nextSelectedAssays =
      validSelectedAssays.length > 0
        ? validSelectedAssays
        : allGeneGroups.assay.filter(assay => modeCompatibleAssays.has(assay));

    if (nextSelectedAssays.length === 0) {
      // Keep assay grouping, but clear invalid explicit selections.
      searchParamsRef.current.delete(QueryParams.GENE_GROUPS);
      setSearchParamsStable(searchParamsRef.current, { replace: true });
      return;
    }

    mutateSearchParamsWithSelection(
      QueryParams.GENE_GROUPS,
      searchParamsRef.current,
      allGeneGroups.assay,
      nextSelectedAssays,
      true,
    );
    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, [
    project,
    queryGeneGrouping,
    queryGeneGroups,
    filters.normalisationMode,
    allGeneGroups.assay,
    searchParamsRef,
    setSearchParamsStable,
  ]);

  useEffect(() => {
    // This should never be empty after data is loaded, and triggering this effect before
    // data is loaded would clear the sampling point names from query params
    if (!environmentsImplicitlySelectedWithSampleTypeGroupSelection.length) {
      return;
    }
    const allSampleTypeGroupSmplPntIds = allProjectSamplingPoints.map(e => e.id);
    const initialSamplingPointIds = querySamplingPointIds.length
      ? getNextToggledIdentifiers(
          querySamplingPointIds,
          allSampleTypeGroupSmplPntIds,
          allSampleTypeGroupSmplPntIds,
          true,
        )
      : allProjectSamplingPoints.map(e => e.id);

    mutateSearchParamsWithSelection(
      QueryParams.SAMPLING_POINTS,
      searchParamsRef.current,
      allSampleTypeGroupSmplPntIds,
      initialSamplingPointIds,
      true,
    );
    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, [querySamplingPointIds, environmentsImplicitlySelectedWithSampleTypeGroupSelection, allProjectSamplingPoints]);

  useEffect(() => {
    if (!project) return;
    const allIds = project.chips.map(c => c.id);
    if (!allIds.length) return;
    const initialChipIds = queryChipIds.length ? getNextToggledIdentifiers(queryChipIds, allIds, allIds, true) : allIds;
    mutateSearchParamsWithSelection(QueryParams.CHIPS, searchParamsRef.current, allIds, initialChipIds, true);
    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, [queryChipIds, project]);

  // (RE)SET GENE GROUPS ON PROJECT, ABS MODE OR GROUPING CHANGE
  const queryGeneGroupsRef = useRef(queryGeneGroups);
  queryGeneGroupsRef.current = queryGeneGroups;
  useEffect(() => {
    if (!assaysLoaded || queryGeneGrouping === 'assay') {
      return;
    }
    const groups = queryGeneGroupsRef.current.length
      ? getNextToggledIdentifiers(
          queryGeneGroupsRef.current,
          allGeneGroups[queryGeneGrouping],
          allGeneGroups[queryGeneGrouping],
          true,
        )
      : allGeneGroups[queryGeneGrouping];

    mutateSearchParamsWithSelection(
      QueryParams.GENE_GROUPS,
      searchParamsRef.current,
      allGeneGroups[queryGeneGrouping],
      groups,
      true,
    );
    setSearchParamsStable(searchParamsRef.current, { replace: true });

    // Should be mainly/only triggered on project, abs. mode or grouping change!?
  }, [
    location.pathname, // project change
    filters.abundances, // abs. mode change
    queryGeneGrouping, // gene grouping change (L2 / Lx / assay)
    assaysLoaded, // Loading
    allGeneGroups, // Loading
  ]);

  const setGroupingStable = useCallback((value: GeneGrouping | null) => {
    if (value) {
      searchParamsRef.current.set(QueryParams.GENE_GROUP_GROUPING, value);
      searchParamsRef.current.delete(QueryParams.GENE_GROUPS);
    } else {
      searchParamsRef.current.delete(QueryParams.GENE_GROUP_GROUPING);
    }
    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, []);
  const absoluteModeOn = isConsideredAbsolute(filters.normalisationMode);
  useEffect(() => {
    if (!absoluteModeOn && queryGeneGrouping === sixteenS) {
      setGroupingStable('l2Target');
    }
  }, [absoluteModeOn, queryGeneGrouping, setGroupingStable]);

  const setSampleTypeGroupStable = useCallback(
    (newGroup: SampleTypeGroup, replaceAndClearSmplPnts: boolean) => {
      const validGroup = allSampleGroupTypes.includes(newGroup)
        ? newGroup
        : AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS;
      mutateSamplingPointRelatedSearchParams(searchParamsRef.current, validGroup, replaceAndClearSmplPnts);
      setSearchParamsStable(searchParamsRef.current, { replace: replaceAndClearSmplPnts });
    },
    [setSearchParamsStable, searchParamsRef],
  );

  const allProjectSamplingPointsRefForScope = useRef(allProjectSamplingPoints);
  allProjectSamplingPointsRefForScope.current = allProjectSamplingPoints;
  const setSamplingPointScopeStable = useCallback(
    (newGroup: SampleTypeGroup, samplingPointIds?: number[]) => {
      const validGroup = allSampleGroupTypes.includes(newGroup)
        ? newGroup
        : AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS;
      const wholeGroupSamplingPointIds = filterGroupSamplingPoints(
        allProjectSamplingPointsRefForScope.current,
        validGroup,
      ).map(samplingPoint => samplingPoint.id);

      mutateSamplingPointScopeSearchParams(
        searchParamsRef.current,
        validGroup,
        wholeGroupSamplingPointIds,
        samplingPointIds,
      );
      setSearchParamsStable(searchParamsRef.current, { replace: true });
    },
    [setSearchParamsStable, searchParamsRef],
  );

  const selectedSamplingPointIdsOrderedRef = useRef(filters.selectedSamplingPointIdsOrdered);
  selectedSamplingPointIdsOrderedRef.current = filters.selectedSamplingPointIdsOrdered;
  const allProjectSmplPntsRef = useRef(allProjectSamplingPoints);
  allProjectSmplPntsRef.current = allProjectSamplingPoints;
  const allProjectSmplPntIdsRef = useRef(allProjectSamplingPointIds);
  allProjectSmplPntIdsRef.current = allProjectSamplingPointIds;
  const allProjectChipIdsRef = useRef(allProjectChipIds);
  allProjectChipIdsRef.current = allProjectChipIds;
  const toggleSamplingPointStable = useCallback((id: number | number[] | undefined, removeOldSelections: boolean) => {
    const allSmplPntIds = allProjectSmplPntIdsRef.current;
    const next = id
      ? getNextToggledIdentifiers(id, selectedSamplingPointIdsOrderedRef.current, allSmplPntIds, removeOldSelections)
      : allSmplPntIds;

    mutateSearchParamsWithSelection(
      QueryParams.SAMPLING_POINTS,
      searchParamsRef.current,
      allSmplPntIds,
      next,
      removeOldSelections,
    );

    setSearchParamsStable(searchParamsRef.current, { replace: true });

    // Scroll to the map, when the selected sampling point changes
    const mapElement = document.querySelector('.mapboxgl-map');
    if (mapElement) {
      mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const setChipStable = useCallback((chipId: number | undefined) => {
    const allIds = allProjectChipIdsRef.current;
    const next = chipId ? [chipId] : allIds;
    mutateSearchParamsWithSelection(QueryParams.CHIPS, searchParamsRef.current, allIds, next, true);
    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, []);

  const queryGeneGroupingRef = useRef(queryGeneGrouping);
  queryGeneGroupingRef.current = queryGeneGrouping;
  const selectedTargetsRef = useRef(filters.selectedTargets);
  selectedTargetsRef.current = filters.selectedTargets;
  const allGeneGroupsRef = useRef(allGeneGroups);
  allGeneGroupsRef.current = allGeneGroups;
  const toggleGeneGroupStable = useCallback(
    (
      group: L2Target | L2Target[] | AssayStr,
      removeOldSelections: boolean,
      selectedGroupingInCaseStateHasNotUpdated?: GeneGrouping,
    ) => {
      const realSelectedGrouping = selectedGroupingInCaseStateHasNotUpdated ?? queryGeneGroupingRef.current;
      const next = getNextToggledIdentifiers<L2Target | AssayStr>(
        group,
        selectedTargetsRef.current,
        allGeneGroupsRef.current[realSelectedGrouping] as L2Target[], // TODO Fix typing
        removeOldSelections,
      );
      mutateSearchParamsWithSelection(
        QueryParams.GENE_GROUPS,
        searchParamsRef.current,
        allGeneGroupsRef.current[realSelectedGrouping],
        next,
        removeOldSelections,
      );

      // TODO IS THIS NEEDED?

      // We are selecting OR unselecting an assay
      // If we have assay in the next selection, set grouping to 'assay'
      // If we don't have assay in the next selection, remove the grouping
      // If we are selecting non-assay, but had previously assay selected

      // const selectedAssay = typeof group === 'string' ? getAssayInfo(group) : undefined;
      // if (selectedAssay) {
      //   if (next.length === 1 && next[0] === selectedAssay.assay) {
      //     searchParamsRef.current.set(QueryParams.GENE_GROUP_GROUPING, 'assay');
      //   } else {
      //     searchParamsRef.current.delete(QueryParams.GENE_GROUP_GROUPING);
      //   }
      // } else if (searchParamsRef.current.get(QueryParams.GENE_GROUP_GROUPING) === 'assay') {
      //   searchParamsRef.current.delete(QueryParams.GENE_GROUP_GROUPING);
      // }

      setSearchParamsStable(searchParamsRef.current, { replace: true });
    },
    [],
  );

  const focusSamplesByUID = useCallback(
    (samplesByUID: FullSamplesByUID) => {
      if (!assaysLoaded) {
        return undefined;
      }
      return filterSamplesAndAbundances(samplesByUID, filters, getGroup);
    },
    [assaysLoaded, getGroup, filters],
  ); // Note: this is intentionally and unstable callback!

  const setNormalisationModeStable = useCallback((value: NormalisationMode) => {
    searchParamsRef.current.set(QueryParams.NORMALISATION_MODE, value);
    setSearchParamsStable(searchParamsRef.current, { replace: true });
  }, []);

  const resetFiltersStable = useCallback(() => {
    setSearchParamsStable({}, { replace: true });
    setAbundanceModeStable(defaultAbundanceMode);
  }, []);

  const setIntervalStable = useCallback(
    (requestedStart: Date | StandardDateFormat, requestedEnd: Date | StandardDateFormat) => {
      const snappedStart = ensureLocalValidMidnightOrDefault(requestedStart, DEFAULT_START_INTERVAL);
      const snappedEnd = ensureLocalValidMidnightOrDefault(requestedEnd, DEFAULT_END_INTERVAL);

      // NOTE: We do not want to use snappedStart and snappedEnd for comparison here, because it distorts the time of the dates
      if (new Date(requestedStart).getTime() === DEFAULT_START_INTERVAL.getTime()) {
        searchParamsRef.current.delete('start');
      } else {
        searchParamsRef.current.set('start', format(snappedStart, 'yyyy-MM-dd'));
      }
      if (new Date(requestedEnd).getTime() === DEFAULT_END_INTERVAL.getTime()) {
        searchParamsRef.current.delete('end');
      } else {
        searchParamsRef.current.set('end', format(snappedEnd, 'yyyy-MM-dd'));
      }
      setSearchParamsStable(searchParamsRef.current, { replace: true });
    },
    [],
  );

  const setMonthStable = useCallback((date: Date | null, endDate: Date | null) => {
    if (date === null) {
      setIntervalStable(DEFAULT_START_INTERVAL, DEFAULT_END_INTERVAL);
    } else {
      const startOfMonth = ensureLocalStartOfMonth(date);
      const startOfNextMonth = ensureLocalStartOfNextMonth(startOfMonth);
      setIntervalStable(startOfMonth, endDate ?? startOfNextMonth);
    }
  }, []);

  const toggleSingleTarget = useCallback(
    (target: string) => {
      const isCurrentlySelected = filters.selectedTargets.length === 1 && filters.selectedTargets[0] === target;
      const isAssay = target.startsWith('AY');
      const grouping = !isCurrentlySelected && isAssay ? 'assay' : 'l2Target';
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-confusing-void-expression
      if (!isAssay && !get(L2Targets, target, undefined)) {
        console.error(`Unexpected toggle target: ${target}`);
      }
      setGroupingStable(grouping);
      toggleGeneGroupStable(target as L2Target | AssayStr, true, grouping);
    },
    [filters.selectedTargets],
  );

  const allProjectSamplingPointNamesRef = useRef(allProjectSamplingPointNames);
  allProjectSamplingPointNamesRef.current = allProjectSamplingPointNames;
  const allProjectSamplingPointsRef = useRef(allProjectSamplingPoints);
  allProjectSamplingPointsRef.current = allProjectSamplingPoints;
  const { selectedSamplingPointIdsOrdered } = filters;
  const setMatchingSamplingPointsAcrossTypes = useCallback(
    (search: string) => {
      const newlyMatchedSmplPntNames = getMatchingStringsIgnoreCase(search, allProjectSamplingPointNamesRef.current);
      if (!search || !newlyMatchedSmplPntNames.length) {
        return;
      }

      const envsByName = groupBy(allProjectSamplingPointsRef.current, e => e.name);
      const newlyMatchedSmplPnts = newlyMatchedSmplPntNames.map(name => envsByName[name]).filter(Boolean);
      const newlyMatchedSmplPntIds = newlyMatchedSmplPnts.map(e => e.id);

      const allNewlyMatchedSampleTypes = uniq(newlyMatchedSmplPnts.map(e => e.type));
      setSampleTypeGroupStable(
        allNewlyMatchedSampleTypes.length > 1
          ? allNewlyMatchedSampleTypes[0]
          : AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS,
        false,
      );

      const allSmplPntIdsOfTheSelectedTypes = allProjectSamplingPointsRef.current
        .filter(e => allNewlyMatchedSampleTypes.includes(e.type))
        .map(e => e.id);

      const next = getNextToggledIdentifiers(
        newlyMatchedSmplPntIds,
        selectedSamplingPointIdsOrdered,
        allSmplPntIdsOfTheSelectedTypes,
        true,
      );
      mutateSearchParamsWithSelection(
        QueryParams.SAMPLING_POINTS,
        searchParamsRef.current,
        allSmplPntIdsOfTheSelectedTypes,
        next,
        true,
      );
      setSearchParamsStable(searchParamsRef.current, { replace: true });
    },
    [selectedSamplingPointIdsOrdered],
  );

  // Set default X-axis mode only after the project has loaded. Avoid defaulting too early so that
  // the project-specific default (stored in the project) is respected. If the user has an explicit
  // query-param value, that will already be present in `filters.xAxisMode` and will not be overridden.
  const defaultXAxisMode = project ? project.defaultXAxisMode : undefined;
  const setXAxisModeStable = useCallback(
    (mode: XAxisMode) => {
      mutateSearchParamsWithSelection(
        QueryParams.X_AXIS_MODE,
        searchParamsRef.current,
        values(XAxisMode),
        [mode],
        true,
      );
      setSearchParamsStable(searchParamsRef.current, { replace: true });
    },
    [searchParamsRef, setSearchParamsStable],
  );
  useEffect(() => {
    // Apply the project-specific default X-axis mode only when the URL lacks an explicit
    // xAxis parameter. This preserves user intent while still honouring project defaults.
    if (!rawXAxisModeParam && defaultXAxisMode && defaultXAxisMode !== filters.xAxisMode) {
      setXAxisModeStable(defaultXAxisMode);
    }
  }, [defaultXAxisMode, rawXAxisModeParam, filters.xAxisMode]);

  const hasFocus = Boolean(
    queryGeneGrouping !== 'l2Target' ||
      filters.selectedTargets.length < allGeneGroups[queryGeneGrouping].length ||
      // NOTE: this doesn't check whether all project envs are within a selected custom group, but just conservatively assumes there may be focus
      // TODO: fix if it turns out to be an issue
      (filters.selectedSampleTypeGroup !== AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS &&
        !(allProjectSampleTypes.length === 1 && filters.selectedSampleTypeGroup === allProjectSampleTypes[0])) ||
      (filters.selectedSamplingPointIdsOrdered.length &&
        filters.selectedSamplingPointIdsOrdered.length < allProjectSamplingPointIds.length) ||
      (filters.chips.length && filters.chips.length < allProjectChipIds.length) ||
      filters.interval.start.getTime() !== DEFAULT_START_INTERVAL.getTime() ||
      filters.interval.end.getTime() !== DEFAULT_END_INTERVAL.getTime(),
  );

  // Memoized so that context consumers can rely on referential stability between unrelated renders
  const queryFilters = useMemo<QueryFilters>(
    () => ({
      toggleGeneGroupStable,
      setGroupingStable,
      filters,
      setAbundanceModeStable,
      setMatchingSamplingPointsAcrossTypes,
      toggleSamplingPointStable,
      setChipStable,
      setSampleTypeGroupStable,
      setSamplingPointScopeStable,
      setNormalisationModeStable,
      setXAxisModeStable,
      hasFocus,
      resetFiltersStable,
      setIntervalStable,
      setMonthStable,
      toggleSingleTarget,
    }),
    [
      toggleGeneGroupStable,
      setGroupingStable,
      filters,
      setAbundanceModeStable,
      setMatchingSamplingPointsAcrossTypes,
      toggleSamplingPointStable,
      setChipStable,
      setSampleTypeGroupStable,
      setSamplingPointScopeStable,
      setNormalisationModeStable,
      setXAxisModeStable,
      hasFocus,
      resetFiltersStable,
      setIntervalStable,
      setMonthStable,
      toggleSingleTarget,
    ],
  );

  return {
    focusSamplesByUID,
    queryFilters,
  };
}

export function getMatchingStringsIgnoreCase(searchString: string, stringsToMatchTo: string[]) {
  const lowerSearchString = searchString.toLowerCase();
  return stringsToMatchTo.filter(str => str.toLowerCase().includes(lowerSearchString));
}
